import HighchartsReact from "highcharts-react-official";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_COLUMN_PLOT_OPTIONS,
    DEFAULT_TOOLTIP_PROPS,
} from "../../consts";
import { WindowSizeState, useWindowSize } from "~/utils/useWindowSize";
import Highcharts from "highcharts/highstock";
import { formatXAxisTick } from "../functions/formatXAxisTick";
import {
    TimeSeriesPoint,
    TimeSeriesItem,
    TimeSeriesUnits,
    TimeSeriesView,
    ChartTypes,
} from "~/modules/token_timeseries/types";
import ReactDOMServer from "react-dom/server";
import { formatApy } from "~/utils/functions/formatApy";
import { hexToRGB } from "~/utils/hexToRGB";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { LegendProps } from "./types";
import { DEFAULT_TIMESERIES_LEGEND_PROPS } from "./consts";
import {
    ColumnPlotOptions,
    MetricsToTimeSeriesViewFunction,
    NavigatorControlState,
    YAxisConfig,
    YAxisTypes,
} from "../Timeseries/types";
import { DEFAULT_NAVIGATOR_CONTROLS } from "../Timeseries/consts";
import { BroadridgeTooltipContent } from "./BroadridgeTooltipContent";
import { RWA_COLORS } from "~/styles/colors";
import { roundN } from "~/utils/functions/roundN";

// // // //

// New types for dual-axis support
interface DualAxisConfig {
    primary: YAxisConfig & {
        title?: string;
        unit?: TimeSeriesUnits;
        format?: string;
    };
    secondary: YAxisConfig & {
        title?: string;
        unit?: TimeSeriesUnits;
        format?: string;
        opposite?: boolean;
    };
}

interface DualAxisTimeSeriesView extends TimeSeriesView {
    yAxis?: 0 | 1; // 0 for primary axis, 1 for secondary axis
}

/**
 * Finds the `TimeSeriesView` with the most entries in the `item.points` array.
 * @param {TimeSeriesView[]} series - Array of `TimeSeriesView` objects.
 * @returns {TimeSeriesView | undefined} The `TimeSeriesView` with the most points or `undefined` if the array is empty.
 */
function findLongestSeries(
    series: TimeSeriesView[],
): TimeSeriesView | undefined {
    if (series.length === 0) {
        return undefined;
    }

    let maxView = series[0]; // Start with the first view as the maximum
    let maxPointsLength = maxView.item.points.length; // Length of points in the first view

    for (let i = 1; i < series.length; i++) {
        const currentView = series[i];
        const currentPointsLength = currentView.item.points.length;
        if (currentPointsLength > maxPointsLength) {
            maxView = currentView;
            maxPointsLength = currentPointsLength;
        }
    }

    return maxView;
}

// // // //

// Sort the items so largest-latest-value is at the top of the chart
export function sortSeries(metrics: TimeSeriesView[]): TimeSeriesView[] {
    return metrics.sort((a, b) => {
        const lastValueA = a.item.points[a.item.points.length - 1][1];
        const lastValueB = b.item.points[b.item.points.length - 1][1];

        return lastValueB - lastValueA;
    });
}

// // // //

// Sort the items so largest-latest-value is at the top of the chart
export function sortMetrics(metrics: TimeSeriesItem[]): TimeSeriesItem[] {
    return metrics.sort((a, b) => {
        const lastValueA = a.points[a.points.length - 1][1];
        const lastValueB = b.points[b.points.length - 1][1];

        return lastValueB - lastValueA;
    });
}

// // // //

// NOTE - this is a copy of the function in formatCurrencyValueCompact.ts
// but with the maximumFractionDigits and minimumFractionDigits set to 0
// to avoid the decimal point being displayed
function formatCurrencyValueCompact(value: number): string {
    // Positive or negative?
    const absValue = Math.abs(value);
    const isNegative = absValue !== value;

    // Handle positive/negative signage
    const signage = isNegative ? "-" : "";

    // Handle billion or greater
    if (absValue >= 1_000_000_000) {
        return `${signage}${roundN(absValue / 1_000_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (absValue >= 1_000_000) {
        return `${signage}${roundN(absValue / 1_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}M`;
    }
    // Handle thousand or greater but less than a million
    else {
        return `${signage}${roundN(absValue / 1_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}K`;
    }
}

function formatAxisValue(
    value: number,
    unit: TimeSeriesUnits,
    customFormat?: string,
    stacked: boolean = false,
): string {
    if (customFormat) {
        return customFormat.replace("{value}", value.toString());
    }

    // Support stacked percent layout
    if (stacked) {
        return `${value}%`;
    }

    // Return dollars format
    if (unit === TimeSeriesUnits.dollar || unit === TimeSeriesUnits.dollars) {
        return `$${formatCurrencyValueCompact(value)}`;
    }

    // Return percent format
    if (unit === TimeSeriesUnits.percent) {
        return `${formatApy(value).toString()}%`;
    }

    // Return tokens format
    if (unit === TimeSeriesUnits.token) {
        return formatCurrencyValueCompact(value);
    }

    // Return years format
    if (unit === TimeSeriesUnits.year) {
        return `${value} years`;
    }

    // Return address format
    if (unit === TimeSeriesUnits.address) {
        return `${value.toLocaleString()}`;
    }

    // Ensure numeric before invoking toLocaleString
    if (typeof value === "number") {
        return value.toLocaleString();
    }

    return String(value);
}

function getChartOptions(props: {
    metrics: DualAxisTimeSeriesView[];
    windowSize: WindowSizeState;
    area: boolean;
    stacked: boolean;
    enableZoom: boolean;
    navigator: NavigatorControlState;
    height: number;
    legend: LegendProps;
    showTotal: boolean;
    yAxis: YAxisConfig | DualAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    maxTooltipEntries?: number;
    isDualAxis?: boolean;
}) {
    const {
        metrics,
        windowSize,
        enableZoom = false,
        area,
        stacked,
        navigator,
        height,
        legend,
        showTotal = false,
        yAxis,
        columnPlotOptions,
        maxTooltipEntries,
        isDualAxis = false,
    } = props;

    // Determine units (default to dollars just to be safe incase metrics[0] is empty)
    const defaultUnit: TimeSeriesUnits = metrics[0]
        ? metrics[0].item.measure.unit
        : TimeSeriesUnits.dollar;

    // Hide legend on small screens
    let showLegend = legend.enabled || false;
    if (windowSize.isSm) {
        showLegend = false;
    }

    const xAxisMin = navigator.xAxis ? navigator.xAxis.min : undefined;
    const xAxisMax = navigator.xAxis ? navigator.xAxis.max : undefined;

    const chart: any = {
        height,
        backgroundColor: "transparent",
        animation: false, // Disable animation
        zooming: {
            type: enableZoom ? "xy" : undefined,
            mouseWheel: {
                enabled: false, // Disable scroll-to-zoom
            },
        },
    };

    if (area && !isDualAxis) {
        chart.type = "area";
    }

    // Define navigatorConfig for Highcharts
    const navigatorConfig: {
        enabled?: boolean;
        series?: undefined | any;
    } = {
        enabled: navigator.active,
    };

    // If navigator is enabled, select the longest series to function as the basline for the navigator display
    if (navigator.enabled && navigator.active) {
        // Find longest series
        const longestSeries = findLongestSeries(metrics);

        // Use series for navigator display if available
        if (longestSeries) {
            navigatorConfig.series = {
                type: "area",
                color: RWA_COLORS.blue[500],
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1,
                    },
                    stops: [
                        [0, hexToRGB(RWA_COLORS.blue[500], 0.8)],
                        [1, hexToRGB(RWA_COLORS.blue[500], 0.2)],
                    ],
                },
                data: longestSeries.item.points.map((v: TimeSeriesPoint) => {
                    return [new Date(v[0]).getTime(), Number(v[1])];
                }),
            };
        }
    }

    // Configure Y-Axis(es)
    let yAxisConfig: any;

    if (isDualAxis && "primary" in yAxis) {
        const dualAxis = yAxis as DualAxisConfig;
        const zeroPlotLine = {
            value: 0,
            color: RWA_COLORS.slate["300"],
            width: 1,
            zIndex: 5,
            dashStyle: "Dash" as const,
        };

        yAxisConfig = [
            {
                // Primary Y-Axis
                min: dualAxis.primary.min,
                type: dualAxis.primary.type,
                ...DEFAULT_AXIS_PROPS,
                plotLines: [zeroPlotLine],
                title: {
                    text: dualAxis.primary.title ?? "Primary Axis",
                },
                labels: {
                    ...DEFAULT_AXIS_LABEL_PROPS,
                    enabled:
                        dualAxis.primary.enabled !== undefined
                            ? dualAxis.primary.enabled
                            : true,
                    formatter: function (): string {
                        // @ts-ignore
                        const value = this.value;
                        return formatAxisValue(
                            value,
                            dualAxis.primary.unit || defaultUnit,
                            dualAxis.primary.format,
                            stacked,
                        );
                    },
                },
            },
            {
                // Secondary Y-Axis
                min: dualAxis.secondary.min,
                type: dualAxis.secondary.type,
                ...DEFAULT_AXIS_PROPS,
                plotLines: [zeroPlotLine],
                title: {
                    text: dualAxis.secondary.title ?? "Secondary Axis",
                },
                opposite:
                    dualAxis.secondary.opposite !== undefined
                        ? dualAxis.secondary.opposite
                        : true,
                labels: {
                    ...DEFAULT_AXIS_LABEL_PROPS,
                    enabled:
                        dualAxis.secondary.enabled !== undefined
                            ? dualAxis.secondary.enabled
                            : true,
                    formatter: function (): string {
                        // @ts-ignore
                        const value = this.value;
                        return formatAxisValue(
                            value,
                            dualAxis.secondary.unit || defaultUnit,
                            dualAxis.secondary.format,
                            stacked,
                        );
                    },
                },
            },
        ];
    } else {
        // Single Y-Axis (original implementation)
        const singleAxis = yAxis as YAxisConfig;
        yAxisConfig = {
            min: singleAxis.min,
            type: singleAxis.type,
            ...DEFAULT_AXIS_PROPS,
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                enabled:
                    singleAxis.enabled !== undefined
                        ? singleAxis.enabled
                        : true,
                formatter: function (): string {
                    // @ts-ignore
                    const value = this.value;
                    return formatAxisValue(
                        value,
                        defaultUnit,
                        undefined,
                        stacked,
                    );
                },
            },
        };
    }

    const opts = {
        title: null,
        chart,
        tooltip: {
            useHTML: true,
            shared: true,
            outside: true,
            style: {
                zIndex: 225,
            },
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const point = this.point;
                const xAxisDate = new Date(point.category);
                const xAxisLabel = formatXAxisTick(xAxisDate);
                const time = xAxisDate.getTime();

                // Collect name of all series that are enabled
                const selectedSeriesNames = this.points.map(
                    (p: any) => p.series.name,
                );

                // NOTE - isn't very efficient
                const allMetrics = metrics.map((m) => m.item);
                const matchedMetrics = allMetrics
                    .filter((m) => {
                        return selectedSeriesNames.includes(m.group.name);
                    })
                    .map((m) => {
                        return {
                            ...m,
                            points: m.points.filter((p) => {
                                return new Date(p[0]).getTime() === time;
                            }),
                        };
                    })
                    .filter((m) => m.points.length > 0);

                return ReactDOMServer.renderToString(
                    <BroadridgeTooltipContent
                        x={xAxisLabel}
                        data={matchedMetrics}
                        unit={defaultUnit}
                        stacked={stacked}
                        showTotal={showTotal}
                        maxTooltipEntries={maxTooltipEntries}
                    />,
                );
            },
        },
        yAxis: yAxisConfig,
        xAxis: {
            type: "datetime",
            ...DEFAULT_AXIS_PROPS,
            min: xAxisMin,
            max: xAxisMax,
            crosshair: true,
            ordinal: true, // Exclude x-axis values where there's no data present
            // startOnTick: true, // Force axis to start on a tick
            // endOnTick: true, // Force axis to end on a tick
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                formatter: function (): string {
                    // @ts-ignore
                    const value = this.value;
                    return formatXAxisTick(new Date(value));
                },
            },
        },
        plotOptions: {
            series: {
                type: "spline",
                animation: {
                    duration: 0,
                },
                events: {
                    legendItemClick: function () {
                        return true;
                    },
                },
            },
        },
        credits: {
            enabled: false,
        },
        legend: {
            ...legend,
            enabled: showLegend,
        },
        navigator: navigatorConfig,
        rangeSelector: {
            enabled: false,
        },
        series: [
            ...metrics
                .filter((m) => m.enabled)
                .map((m) => {
                    const color = m.item.group.color || "#999999";

                    const seriesConfig: any = {
                        name: `${m.item.group.name}`,
                        tooltip: {
                            valueDecimals: 2,
                        },
                        type: m.chartType,
                        // Make line / area charts thicker (2px), keep columns at default
                        lineWidth:
                            m.chartType === ChartTypes.line ||
                            m.chartType === ChartTypes.area
                                ? 2
                                : 1,
                        color:
                            m.chartType === ChartTypes.column
                                ? hexToRGB(color, 0.6)
                                : color,
                        data: m.item.points.map((v: TimeSeriesPoint) => {
                            if (m.invertYAxis) {
                                return [
                                    new Date(v[0]).getTime(),
                                    Number(v[1]) * -1,
                                ];
                            }
                            return [new Date(v[0]).getTime(), Number(v[1])];
                        }),
                        fillColor:
                            m.chartType === ChartTypes.area && isDualAxis
                                ? {
                                      linearGradient: {
                                          x1: 0,
                                          y1: 0,
                                          x2: 0,
                                          y2: 1,
                                      },
                                      stops: [
                                          [0, hexToRGB(color, 0.2)],
                                          [1, hexToRGB(color, 0)],
                                      ],
                                  }
                                : area && !isDualAxis
                                  ? {
                                        linearGradient: {
                                            x1: 0,
                                            y1: 0,
                                            x2: 0,
                                            y2: 1,
                                        },
                                        stops: [
                                            [0, hexToRGB(color, 0.8)],
                                            [1, hexToRGB(color, 0.2)],
                                        ],
                                    }
                                  : undefined,
                    };

                    // Add yAxis specification for dual-axis charts
                    if (isDualAxis && m.yAxis !== undefined) {
                        seriesConfig.yAxis = m.yAxis;
                    }

                    return seriesConfig;
                }),
        ],
    };

    // // // //

    if (area && !isDualAxis) {
        // @ts-ignore
        opts.plotOptions.area = {
            stacking: stacked ? "percent" : "overlap",
            marker: {
                enabled: false,
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        };
    }

    if (isDualAxis && metrics.some((m) => m.chartType === ChartTypes.area)) {
        // @ts-ignore
        opts.plotOptions.area = {
            stacking: "overlap",
            marker: {
                enabled: false,
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        };
    }

    // Handle column plot options - ensure side-by-side display
    if (
        columnPlotOptions ||
        metrics.some((m) => m.chartType === ChartTypes.column)
    ) {
        let columnPlotOptionsValue: ColumnPlotOptions = {
            ...DEFAULT_COLUMN_PLOT_OPTIONS,
        };

        if (columnPlotOptions) {
            columnPlotOptionsValue = {
                ...columnPlotOptionsValue,
                ...columnPlotOptions,
            };
        }

        // @ts-ignore
        opts.plotOptions.column = {
            ...columnPlotOptionsValue,
            // Ensure side-by-side display with tight grouping
            stacking: null,
            grouping: true,
            pointPadding: 0.02, // Very small padding - columns almost touching
            groupPadding: 0.15, // Slightly larger gap between time periods
        };
    }

    return opts;
}

// // // //
// Define interfaces for component props

interface MetricsProps {
    metrics: TimeSeriesItem[];
    series?: never;
}

interface SeriesProps {
    metrics?: never;
    series: DualAxisTimeSeriesView[];
}

type DataProps = MetricsProps | SeriesProps;

interface Props {
    area?: boolean;
    stacked?: boolean;
    legend?: LegendProps;
    enableZoom?: boolean;
    showTotal?: boolean;
    height?: number;
    navigator?: NavigatorControlState;
    yAxis?: YAxisConfig | DualAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    maxTooltipEntries?: number;
    metricsToTimeSeriesView?: MetricsToTimeSeriesViewFunction;
    isDualAxis?: boolean;
}

/**
 * TimeSeriesChart
 * Renders chart for historical metrics with support for dual-axis configuration
 */
export function TimeSeriesChartLayout(props: Props & DataProps) {
    const {
        metrics,
        series,
        area = false,
        stacked = false,
        enableZoom = false,
        navigator = DEFAULT_NAVIGATOR_CONTROLS,
        showTotal = false,
        legend = { ...DEFAULT_TIMESERIES_LEGEND_PROPS },
        height = 300,
        columnPlotOptions,
        maxTooltipEntries = 25,
        metricsToTimeSeriesView = null,
        isDualAxis = false,
        yAxis = {
            min: 0,
            enabled: true,
            type: YAxisTypes.linear,
        },
    } = props;

    // Handle both TimeSeriesView[] + TimeSeriesItem[] props
    let chartSeries: DualAxisTimeSeriesView[] = [];
    if (series) {
        chartSeries = sortSeries(series) as DualAxisTimeSeriesView[];
    } else if (metricsToTimeSeriesView !== null) {
        chartSeries = metricsToTimeSeriesView(
            sortMetrics(metrics),
        ) as DualAxisTimeSeriesView[];
    } else {
        chartSeries = sortSeries(
            metrics.map((m) => {
                return {
                    item: m,
                    enabled: true,
                    chartType: ChartTypes.area,
                } as DualAxisTimeSeriesView;
            }),
        );
    }

    const windowSize = useWindowSize();

    if (
        chartSeries.length === 0 ||
        (chartSeries.length === 1 && chartSeries[0].item.points.length < 2)
    ) {
        return (
            <div
                style={{ height: `${height}px` }}
                className="flex flex-col items-center justify-center gap-3 text-muted-foreground bg-card"
            >
                <FontAwesomeIcon icon={faChartLine} className="h-8 w-8" />
                <p className="text-sm font-light">
                    No data available for this selection
                </p>
            </div>
        );
    }

    const options = getChartOptions({
        metrics: chartSeries,
        windowSize,
        area,
        stacked,
        enableZoom,
        navigator,
        height,
        showTotal,
        legend,
        columnPlotOptions,
        yAxis,
        maxTooltipEntries,
        isDualAxis,
    });

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export const TimeSeriesChart = React.memo(TimeSeriesChartLayout);
